import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { setProgress, setToast } from "../../../../redux/slice/system";
import { UploadAPI, SettingAPI } from "../../../../axios/api";
import {
  useAbortedEffect,
  useSearch,
  useTable,
  useUploadXlsx,
  usePop,
  useConfirm,
} from "../../../../components/hooks";
import moment from "moment";

import ModuleBreadCrumb from "../../../../components/breadCrumb";
import ModulePopLeaveWorkDetail from "./PopDetail";
import {
  formatOptions,
  setPageSetting,
  formatNumber,
} from "../../../../service/common";
import { system_dropdown, system_table } from "../../../../service/system";
import { option_enabled } from "../../../../service/option";

import { classNames } from "primereact/utils";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";

export default function BackUploadLeaveWorkList() {
  const route_navigate = useNavigate();
  const dispatch = useDispatch();
  const crumbItem = [{ key: 1, label: "請假管理" }];
  const [reload, setReload] = useState({ list: false });
  const {
    setTbData,
    tbTotalRecords,
    setTbTotalRecords,
    setTbFirstIndex,
    setTbRows,
    setTbColumns,
    renderTable,
    setPageAction,
  } = useTable();
  // 篩選條件
  const defaultFilter = {
      page: 1,
      size: system_table.size,
      keywords: "",
    },
    pagePath = "/upload/leaveWork";
  const {
    filterData,
    setFilterData,
    search,
    setSearch,
    changeFilter,
    renderSearchWrap,
  } = useSearch({
    defaultFilter,
    pagePath: [pagePath],
    tbSetting: { setTbFirstIndex, setTbRows },
    reloadAction: () => setReload((state) => ({ ...state, list: true })),
  });
  const { renderUpload } = useUploadXlsx({
    action: {
      format: (data) => formatAction(data),
      upload: (data) =>
        UploadAPI.importsLeaveWorksRecordData({
          data,
        }),
      reload: () => setReload((state) => ({ ...state, list: true })),
    },
  });
  const { isPop, popOption, openPop, closePop, renderDialog } = usePop([
    {
      key: "detail",
      title: () => "明細",
      isOpen: false,
    },
  ]);
  const { confirmOption, setConfirmOption, renderConfirm } = useConfirm();
  const [leaveType, setLeaveType] = useState([]);
  const uploadColumns = [
    {
      key: "company_code",
      label: "公司編號",
    },
    {
      key: "department_name",
      label: "部門",
    },
    {
      key: "username",
      label: "員工編號",
    },
    {
      key: "name",
      label: "員工姓名",
    },
    {
      key: "leave_work_start_datetime",
      label: "請假開始時間",
      type: "date",
    },
    {
      key: "leave_work_end_datetime",
      label: "請假結束時間",
      type: "date",
    },
    {
      key: "total_leave_work_times",
      label: "請假總時數",
    },
    {
      key: "type_name",
      label: "假別",
      isRequired: true,
    },
    {
      key: "remark",
      label: "備註",
    },
    {
      key: "apply_datetime",
      label: "申請日",
    },
    {
      key: "status_name",
      label: "狀態",
    },
  ];

  useEffect(() => {
    const action = ({ startIndex, rows }) => {
      let temp = {
        ...search,
        page: Math.floor(startIndex / rows) + 1,
        size: rows,
      };
      setFilterData(temp);
      setSearch(temp);
    };
    setPageAction(
      () =>
        ({ startIndex, rows }) =>
          action({ startIndex, rows })
    );
  }, [search]);

  useAbortedEffect((signal) => {
    let params = { type: 2, page: -1, size: -1 };

    SettingAPI.getAttendanceLeaveScoreType({
      data: params,
      options: { signal },
    }).then((result) => {
      let { Response, data, message } = result;
      if (Response === 1) {
        setLeaveType(data);
      } else {
        dispatch(
          setToast({
            severity: "error",
            summary: message,
            detail: "",
          })
        );
      }
    });
  }, []);

  // 取得列表資料
  useAbortedEffect(
    (signal) => {
      if (!reload.list) return;

      dispatch(setProgress(true));

      setPageSetting({ params: search, page: pagePath });

      let params = {
        ...search,
      };

      UploadAPI.getLeaveWorksRecordList({
        data: params,
        options: { signal },
      }).then((result) => {
        let { Response, data, total, message } = result;

        if (Response === 1) {
          setTbData(data);
          setTbTotalRecords(total);
          setReload((state) => ({
            ...state,
            list: false,
          }));
        } else {
          dispatch(
            setToast({
              severity: "error",
              summary: message,
              detail: "",
            })
          );
        }

        dispatch(setProgress(false));
      });
    },
    [reload]
  );

  useEffect(() => {
    const renderBtnAction = (data) => (
      <>
        <Button
          className="p-button-sm"
          type="button"
          label="查看"
          onClick={() => openPop({ type: "detail", data })}
        />
        <Button
          id={`button_delete_${data.id}`}
          className="p-button-danger p-button-sm ml-2"
          type="button"
          label="刪除"
          onClick={() =>
            setConfirmOption({
              setting: {
                target: document.getElementById(`button_delete_${data.id}`),
                message: "確認是否刪除?",
                icon: "pi pi-exclamation-triangle",
                visible: !confirmOption.setting.visible,
                acceptLabel: "是",
                rejectLabel: "否",
                accept: () => deleteAction({ data }),
                reject: () => null,
              },
              reOpen: confirmOption.setting.visible,
            })
          }
        />
      </>
    );

    const renderDate = (data) => (
      <>
        {data.leave_work_start_datetime}-{data.leave_work_end_datetime}
      </>
    );

    setTbColumns([
      {
        field: "department_name",
        header: "部門",
        className: "flex-grow-1",
        style: { flexBasis: "70px" },
      },
      {
        field: "username",
        header: "員工編號",
        className: "flex-grow-1",
        style: { flexBasis: "70px" },
      },
      {
        field: "name",
        header: "員工姓名",
        className: "flex-grow-1",
        style: { flexBasis: "70px" },
      },
      {
        field: "name",
        header: "請假起訖時間",
        className: "w-11rem",
        body: (data) => renderDate(data),
      },
      {
        field: "total_leave_work_times",
        header: "請假總時數",
        className: "flex-grow-1",
        style: { flexBasis: "70px" },
      },
      {
        field: "type_name",
        header: "假別",
        className: "flex-grow-1",
        style: { flexBasis: "70px" },
      },
      {
        field: "id",
        header: "進階",
        className: "w-9rem froze-right",
        alignFrozen: "right",
        frozen: true,
        body: (data) => renderBtnAction(data),
      },
    ]);
  }, [pagePath]);

  const deleteAction = ({ data }) => {
    dispatch(setProgress(true));

    let jsonData = { data: [data.id] };

    UploadAPI.deleteLeaveWorksRecord({ data: jsonData }).then((result) => {
      let { Response } = result;

      if (Response === 1) {
        dispatch(
          setToast({
            severity: "success",
            summary: "成功刪除",
            detail: "",
          })
        );
        setReload((state) => ({
          ...state,
          list: true,
        }));
      } else {
        dispatch(
          setToast({
            severity: "error",
            summary: "刪除失敗",
            detail: "",
          })
        );
        dispatch(setProgress(false));
      }
    });
  };

  const formatAction = (data) => {
    let isValid = true,
      message = "",
      startIndex = 2,
      allType = leaveType.map((item) => item.name);
    let requireColumns = [],
      noMatchType = [];

    data.splice(0, startIndex);
    data = data.map((item) => {
      let details = Object.values(item);

      if (
        item.__EMPTY_6 &&
        !allType.includes(item.__EMPTY_6) &&
        !noMatchType.includes(item.__EMPTY_6)
      ) {
        isValid = false;
        noMatchType.push(item.__EMPTY_6);
      }

      return {
        ...uploadColumns.reduce((pre, cur, cIdx) => {
          let value = details[cIdx].trim();

          if (
            (value === "" || value === null || value === undefined) &&
            cur.isRequired &&
            !requireColumns.includes(cur.label)
          ) {
            isValid = false;
            requireColumns.push(cur.label);
          }

          return {
            ...pre,
            [cur.key]:
              cur.type === "date"
                ? moment(details[cIdx]).format("YYYY-MM-DD HH:mm")
                : details[cIdx],
          };
        }, {}),
      };
    });

    if (requireColumns.length > 0)
      message += `『${requireColumns.join("、")}』為必填欄位`;
    if (noMatchType.length > 0)
      message += `${message ? "\n" : ""}假別未設定扣分(${noMatchType.join(
        "、"
      )})\n請聯繫資訊人員`;

    return {
      isValid,
      message,
      data,
    };
  };

  return (
    <>
      {renderConfirm}

      <ModuleBreadCrumb crumbItem={crumbItem} />

      <h2 className="my-3 ml-1">請假管理</h2>

      <div className="border-round-lg bg-white p-3">
        {renderSearchWrap({
          isShow: false,
          template: (
            <>
              <div className="col-12 md:col-4 xl:col-3 flex align-items-start flex-column mt-2 md:mt-0">
                <label
                  htmlFor="username"
                  className="w-4rem white-space-nowrap text-left mb-1"
                >
                  關鍵字
                </label>
                <InputText
                  className="w-full"
                  name="keywords"
                  value={filterData.keywords}
                  onChange={(e) => changeFilter(e)}
                />
              </div>
              <div
                className={classNames(
                  "col-12 md:col-8 xl:col-9 flex align-items-end mt-2 md:mt-0"
                )}
              >
                <Button
                  className="p-button-info p-button-sm w-auto ml-auto md:ml-0 md:mt-0 px-3"
                  type="button"
                  icon="pi pi-search"
                  label="查詢"
                  onClick={() => {
                    setSearch({
                      ...filterData,
                      page: 1,
                    });
                    setTbFirstIndex(0);
                  }}
                />

                {renderUpload()}
              </div>
            </>
          ),
        })}

        {renderTable({
          className: "mt-2",
          options: { scrollable: true, scrollDirection: "both" },
        })}
      </div>

      {renderDialog({
        className: "w-11 md:w-10 lg:w-8 max-w-40rem",
        children: (
          <>
            {isPop.detail?.isOpen && (
              <ModulePopLeaveWorkDetail
                popPreOption={popOption}
                closePrePop={closePop}
                uploadColumns={uploadColumns}
                setPreReload={setReload}
                leaveType={leaveType}
              />
            )}
          </>
        ),
      })}
    </>
  );
}
